import React from 'react';
import {
  Create,
  Edit,
  EditButton,
  Filter,
  maxLength,
  maxValue,
  minLength,
  minValue,
  number,
  NumberField,
  NumberInput,
  ReferenceField,
  ReferenceInput,
  required,
  SearchInput,
  SelectInput,
  TextField,
  TextInput,
} from 'react-admin';
import withStyles from '@material-ui/core/styles/withStyles';
import { ZippityDatagrid, ZippitySimpleForm } from '../components';
import ImageLinkField from './ImageLinkField';
import ImageUploadSelector from './ImageUploadSelector';
import LatLngInput from './LatLngInput';
import LatLngField from './LatLngField';
import BulkDeleteButton from '../components/BulkDeleteButton';
import CustomTitle from '../components/CustomTitle';
import ReferenceChild from '../components/ReferenceChild';
import { getZ3PConfigValue, perPage } from '../helper';
import ListWithCSVImport from '../components/ListWithCSVImport';

const KeyoskFilter = (props) => (
  <Filter {...props}>
    <SearchInput source="q" alwaysOn />
    <ReferenceInput
      source="client_location_id"
      reference="client-location"
      sort={{ field: 'long_name', order: 'ASC' }}
      perPage={perPage}
      alwaysOn
    >
      <ReferenceChild
        {...props}
        optionText="long_name"
        source="client_location_id"
      />
    </ReferenceInput>
  </Filter>
);

const listStyles = {};

const isUsingHelpText = getZ3PConfigValue('is_using_help_text');

export const KeyoskList = withStyles(listStyles)(({ classes, ...props }) => (
  <div>
    <ListWithCSVImport
      {...props}
      filters={<KeyoskFilter />}
      bulkActionButtons={<BulkDeleteButton />}
      sort={{ field: 'client_location_id', order: 'ASC' }}
      perPage={50}
    >
      <ZippityDatagrid>
        <TextField source="created_at" />
        <TextField source="modified_at" />
        <TextField source="keyosk_id" />
        <ReferenceField
          source="client_location_id"
          reference="client-location"
          sortable={false}
        >
          <TextField source="short_name" />
        </ReferenceField>
        <TextField source="name" />
        <TextField source="keyosk_type" />
        <LatLngField source="lat-lng" prefix="" />
        <TextField source="blurb" />
        <ImageLinkField source="image_url" />
        <NumberField source="number_of_boxes" />
        <EditButton />
      </ZippityDatagrid>
    </ListWithCSVImport>

    {isUsingHelpText && (
      <div>
        The <strong>Keyosk</strong> is where our customers store/retrieve their
        car keys.
        <ul>
          <li>
            Every Client Location should have at least one Keyosk before we go
            live.
          </li>
          <li>
            If there&apos;s no Keyosk for a location, we use the name
            &quot;Zippity Keyosk&quot;.
          </li>
          <li>
            If there&apos;s no Keyosk Image present, we use the{' '}
            <a
              href="https://zippityinfra.imgix.net/keyosk/generic.jpg"
              target="_blank"
              rel="noopener noreferrer"
            >
              default image
            </a>
            .
          </li>
          <li>
            If there&apos;s no Keyosk blurb, we say: &quot;On the day of your
            service, please drop off your keys at our Keyosk, similar to the one
            in the image above.&quot;
          </li>
          <li>
            The <strong>Name, Blurb</strong> and <strong>Image</strong> are
            displayed to the customer -- both in the booking app, and in
            transactional emails.
          </li>
          <li>
            The <strong>Latitude/Longitude</strong> are used to give the
            customer a Google Maps link. They should have at least 6 digits of
            precision.
          </li>
          <li>
            The <strong>Type</strong> and <strong>Number of Boxes</strong> are
            not yet used.
          </li>
        </ul>
      </div>
    )}
  </div>
));

const editStyles = {};

const validateName = [required(), minLength(2), maxLength(255)];
const validateBlurb = [maxLength(1023)];
const validateBoxes = [required(), number(), minValue(-1), maxValue(100)];

export const KeyoskEdit = withStyles(editStyles)(({ classes, ...props }) => (
  <Edit
    undoable={false}
    title={<CustomTitle title="resources.keyosk.title" />}
    {...props}
  >
    <ZippitySimpleForm>
      <ReferenceInput
        source="client_location_id"
        reference="client-location"
        sort={{ field: 'long_name', order: 'ASC' }}
        perPage={perPage}
        validate={[required()]}
      >
        <ReferenceChild
          {...props}
          optionText="long_name"
          source="client_location_id"
        />
      </ReferenceInput>
      <TextInput validate={validateName} source="name" />
      <LatLngInput prefix="" displayAtTop />
      <SelectInput
        validate={[required()]}
        source="keyosk_type"
        choices={[
          { id: 'no_box', name: 'No Box' },
          { id: 'physical_key', name: 'Physical Key' },
          { id: 'indoor_1', name: 'Indoor' },
          { id: 'outdoor_1', name: 'Outdoor' },
          { id: 'indoor_cloud', name: 'Indoor, Cloud' },
          { id: 'outdoor_cloud', name: 'Outdoor, Cloud' },
        ]}
      />
      <NumberInput validate={validateBoxes} source="number_of_boxes" />
      <TextInput multiline validate={validateBlurb} source="blurb" />
      <ImageUploadSelector source="image_url" folder="keyosk" />
    </ZippitySimpleForm>
  </Edit>
));

export const KeyoskCreate = withStyles(editStyles)(({ classes, ...props }) => (
  <Create {...props}>
    <ZippitySimpleForm>
      <ReferenceInput
        source="client_location_id"
        reference="client-location"
        sort={{ field: 'long_name', order: 'ASC' }}
        perPage={perPage}
        validate={[required()]}
      >
        <ReferenceChild
          {...props}
          optionText="long_name"
          source="client_location_id"
        />
      </ReferenceInput>
      <TextInput validate={validateName} source="name" />
      <LatLngInput prefix="" displayAtTop />
      <SelectInput
        validate={[required()]}
        source="keyosk_type"
        choices={[
          { id: 'no_box', name: 'No Box' },
          { id: 'physical_key', name: 'Physical Key' },
          { id: 'customer_code', name: 'Customer Code' },
          { id: 'preassigned_code', name: 'Preassigned Code' },
        ]}
      />
      <NumberInput
        validate={validateBoxes}
        source="number_of_boxes"
        defaultValue="12"
      />
      <TextInput multiline validate={validateBlurb} source="blurb" />
      <ImageUploadSelector source="image_url" folder="keyosk" />
    </ZippitySimpleForm>
  </Create>
));
